import { useState, useEffect } from "react";
import { apiClient } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Edit2, Trash2, Clock, Layers, FolderPlus } from "lucide-react";
import { toast } from "sonner";

const Shifts = () => {
  const [groups, setGroups] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", description: "", onConfirm: null });
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [saving, setSaving] = useState(false);
  const [groupForm, setGroupForm] = useState({ group_name: "" });
  const [shiftForm, setShiftForm] = useState({
    shift_name: "", start_time: "09:00", end_time: "17:00", group_id: "", expected_break_hours: "0"
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [gRes, sRes] = await Promise.all([
        apiClient.get('/schedule-groups'),
        apiClient.get('/shifts')
      ]);
      setGroups(gRes.data.schedule_groups);
      setShifts(sRes.data.shifts);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ---- Group CRUD ----
  const handleSaveGroup = async () => {
    setSaving(true);
    try {
      if (editingGroup) {
        await apiClient.put(`/schedule-groups/${editingGroup.group_id}`, groupForm);
      } else {
        await apiClient.post('/schedule-groups', groupForm);
      }
      setShowGroupDialog(false);
      setEditingGroup(null);
      setGroupForm({ group_name: "" });
      fetchData();
    } catch (error) {
      console.error("Failed to save group:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    setConfirmDialog({
      open: true, title: "Delete Schedule Group?", description: "This will delete the group and all its shifts. This cannot be undone.",
      onConfirm: async () => {
        try { await apiClient.delete(`/schedule-groups/${groupId}`); fetchData(); } catch (e) { toast.error("Failed to delete group"); }
        setConfirmDialog(d => ({ ...d, open: false }));
      }
    });
  };

  // ---- Shift CRUD ----
  const handleSaveShift = async () => {
    setSaving(true);
    try {
      const payload = { ...shiftForm, expected_break_hours: parseFloat(shiftForm.expected_break_hours || 0) };
      if (editingShift) {
        await apiClient.put(`/shifts/${editingShift.shift_id}`, payload);
      } else {
        await apiClient.post('/shifts', payload);
      }
      setShowShiftDialog(false);
      setEditingShift(null);
      setShiftForm({ shift_name: "", start_time: "09:00", end_time: "17:00", group_id: "", expected_break_hours: "0" });
      fetchData();
    } catch (error) {
      console.error("Failed to save shift:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShift = async (shiftId) => {
    setConfirmDialog({
      open: true, title: "Delete Shift?", description: "This shift will be permanently removed.",
      onConfirm: async () => {
        try { await apiClient.delete(`/shifts/${shiftId}`); fetchData(); } catch (e) { toast.error("Failed to delete shift"); }
        setConfirmDialog(d => ({ ...d, open: false }));
      }
    });
  };

  const formatTime = (time) => {
    if (!time) return "-";
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    return `${h % 12 || 12}:${minutes} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  const calcDuration = (start, end) => {
    if (!start || !end) return "-";
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let hrs = eh - sh; let mins = em - sm;
    if (mins < 0) { hrs--; mins += 60; }
    if (hrs < 0) hrs += 24;
    return `${hrs}h${mins > 0 ? ` ${mins}m` : ''}`;
  };

  const calcDurationNum = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let diff = (eh + em / 60) - (sh + sm / 60);
    if (diff < 0) diff += 24;
    return Math.round(diff * 10) / 10;
  };

  const getGroupName = (groupId) => groups.find(g => g.group_id === groupId)?.group_name || "Unassigned";

  // Group shifts by group
  const groupedShifts = groups.map(group => ({
    ...group,
    shifts: shifts.filter(s => s.group_id === group.group_id)
  }));
  const ungroupedShifts = shifts.filter(s => !s.group_id || !groups.find(g => g.group_id === s.group_id));

  return (
    <Layout>
      <div data-testid="shifts-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">Schedules & Shifts</h1>
            <p className="text-slate-600 mt-1">Create schedule groups (e.g., Kitchen, Front Desk) and define shifts for each.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditingGroup(null); setGroupForm({ group_name: "" }); setShowGroupDialog(true); }} data-testid="add-group-btn">
              <FolderPlus className="w-4 h-4 mr-2" /> New Group
            </Button>
            {groups.length > 0 && (
              <Button className="bg-[#0D9488] hover:bg-[#14B8A6]" onClick={() => { setEditingShift(null); setShiftForm({ shift_name: "", start_time: "09:00", end_time: "17:00", group_id: groups[0]?.group_id || "", expected_break_hours: "0" }); setShowShiftDialog(true); }} data-testid="add-shift-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Shift
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => <div key={i} className="h-32 bg-slate-100 rounded animate-pulse" />)}
          </div>
        ) : groups.length === 0 && shifts.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-300">
            <CardContent className="p-8 text-center">
              <Layers className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#0A192F] mb-2">No schedule groups yet</h3>
              <p className="text-slate-500 mb-4">Create schedule groups like "Kitchen", "Front Desk" — then add shifts to each group.</p>
              <Button onClick={() => { setGroupForm({ group_name: "" }); setShowGroupDialog(true); }} className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="create-first-group-btn">
                <FolderPlus className="w-4 h-4 mr-2" /> Create First Group
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {groupedShifts.map(group => (
              <Card key={group.group_id} className="border border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between py-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Layers className="w-5 h-5 text-[#0D9488]" />
                    {group.group_name}
                    <Badge variant="secondary" className="ml-2">{group.shifts.length} shifts</Badge>
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingGroup(group); setGroupForm({ group_name: group.group_name }); setShowGroupDialog(true); }} data-testid={`edit-group-${group.group_id}`}>
                      <Edit2 className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteGroup(group.group_id)} data-testid={`delete-group-${group.group_id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEditingShift(null); setShiftForm({ shift_name: "", start_time: "09:00", end_time: "17:00", group_id: group.group_id, expected_break_hours: "0" }); setShowShiftDialog(true); }} data-testid={`add-shift-to-${group.group_id}`}>
                      <Plus className="w-4 h-4 mr-1" /> Add Shift
                    </Button>
                  </div>
                </CardHeader>
                {group.shifts.length === 0 ? (
                  <CardContent className="pt-0">
                    <p className="text-sm text-slate-400 text-center py-4">No shifts yet. Add shifts to this group.</p>
                  </CardContent>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Name</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Start</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">End</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Duration</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Break</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Net Hours</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.shifts.map(shift => (
                          <TableRow key={shift.shift_id} data-testid="shift-row">
                            <TableCell className="font-medium text-[#0A192F]">{shift.shift_name}</TableCell>
                            <TableCell>{formatTime(shift.start_time)}</TableCell>
                            <TableCell>{formatTime(shift.end_time)}</TableCell>
                            <TableCell className="font-semibold text-[#0D9488]">{calcDuration(shift.start_time, shift.end_time)}</TableCell>
                            <TableCell className="text-slate-500">{shift.expected_break_hours || 0}h</TableCell>
                            <TableCell className="font-semibold text-[#0A192F]">{Math.max(0, calcDurationNum(shift.start_time, shift.end_time) - (shift.expected_break_hours || 0))}h</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => { setEditingShift(shift); setShiftForm({ shift_name: shift.shift_name, start_time: shift.start_time, end_time: shift.end_time, group_id: shift.group_id || "", expected_break_hours: String(shift.expected_break_hours || 0) }); setShowShiftDialog(true); }} data-testid={`edit-shift-${shift.shift_id}`}>
                                  <Edit2 className="w-4 h-4 text-slate-500" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteShift(shift.shift_id)} data-testid={`delete-shift-${shift.shift_id}`}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            ))}

            {ungroupedShifts.length > 0 && (
              <Card className="border border-slate-200 border-dashed">
                <CardHeader className="py-4">
                  <CardTitle className="text-lg text-slate-400">Unassigned Shifts</CardTitle>
                </CardHeader>
                <div className="overflow-x-auto">
                  <Table>
                    <TableBody>
                      {ungroupedShifts.map(shift => (
                        <TableRow key={shift.shift_id} data-testid="shift-row">
                          <TableCell className="font-medium text-[#0A192F]">{shift.shift_name}</TableCell>
                          <TableCell>{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</TableCell>
                          <TableCell className="font-semibold text-[#0D9488]">{calcDuration(shift.start_time, shift.end_time)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setEditingShift(shift); setShiftForm({ shift_name: shift.shift_name, start_time: shift.start_time, end_time: shift.end_time, group_id: shift.group_id || "", expected_break_hours: String(shift.expected_break_hours || 0) }); setShowShiftDialog(true); }}>
                                <Edit2 className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteShift(shift.shift_id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={(open) => { if (!open) { setShowGroupDialog(false); setEditingGroup(null); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingGroup ? "Edit Group" : "New Schedule Group"}</DialogTitle>
            <DialogDescription>e.g., Kitchen, Front Desk, Bar</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Group Name *</Label>
              <Input value={groupForm.group_name} onChange={(e) => setGroupForm({ group_name: e.target.value })} placeholder="e.g., Kitchen" data-testid="group-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGroupDialog(false); setEditingGroup(null); }}>Cancel</Button>
            <Button onClick={handleSaveGroup} disabled={saving || !groupForm.group_name.trim()} className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="save-group-btn">
              {saving ? "Saving..." : editingGroup ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Dialog */}
      <Dialog open={showShiftDialog} onOpenChange={(open) => { if (!open) { setShowShiftDialog(false); setEditingShift(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingShift ? "Edit Shift" : "Add Shift"}</DialogTitle>
            <DialogDescription>{editingShift ? "Update shift details." : "Create a new shift."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Schedule Group *</Label>
              <Select value={shiftForm.group_id} onValueChange={(val) => setShiftForm({ ...shiftForm, group_id: val })}>
                <SelectTrigger data-testid="shift-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Shift Name *</Label>
              <Input value={shiftForm.shift_name} onChange={(e) => setShiftForm({ ...shiftForm, shift_name: e.target.value })} placeholder="e.g., Morning, Afternoon" data-testid="shift-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })} data-testid="shift-start-input" />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })} data-testid="shift-end-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Expected Break Hours</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="8"
                value={shiftForm.expected_break_hours}
                onChange={(e) => setShiftForm({ ...shiftForm, expected_break_hours: e.target.value })}
                placeholder="0"
                data-testid="shift-break-input"
              />
              <p className="text-xs text-slate-400">How many break hours employees are expected to take during this shift</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between text-sm text-slate-600">
                <span>Duration: <span className="font-semibold text-[#0D9488]">{calcDuration(shiftForm.start_time, shiftForm.end_time)}</span></span>
                <span>Net Hours: <span className="font-semibold text-[#0A192F]">{Math.max(0, calcDurationNum(shiftForm.start_time, shiftForm.end_time) - parseFloat(shiftForm.expected_break_hours || 0))}h</span></span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowShiftDialog(false); setEditingShift(null); }}>Cancel</Button>
            <Button onClick={handleSaveShift} disabled={saving || !shiftForm.shift_name.trim() || !shiftForm.group_id} className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="save-shift-btn">
              {saving ? "Saving..." : editingShift ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog(d => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#0A192F]">{confirmDialog.title}</DialogTitle>
            <DialogDescription className="pt-2">{confirmDialog.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white" onClick={confirmDialog.onConfirm}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Shifts;
